/**
 * Phase Action Atoms - Write operations for phases
 *
 * These atoms handle all phase mutations (create, update, delete, reorder).
 * They update local state optimistically and mark entities as dirty for auto-save.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { atom } from "jotai";
import { _phasesAtom, _phasesInDBAtom, phasesStatusAtom, phasesErrorAtom } from "../core/phases";
import { accessTokenAtom } from "../core/user";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { needsCreation, generatePhaseKey } from "@/frontend/lib/utils/temp-id";
import {
  PhaseSchema,
  type Phase,
  type CreatePhaseBody,
  type UpdatePhaseBody as UpdatePhaseApiPayload,
} from "@/isomorphic/schemas";
import { fetchValidatedArray, postValidated, putValidated, deleteValidated } from "@/frontend/lib/validation";
import { addToHistoryAtom } from "./history-actions";

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Load phases for a workflow
 */
export const loadWorkflowPhasesAtom = atom(null, async (get, set, workflowId: string) => {
  const jwt = get(accessTokenAtom);
  if (!jwt) {
    console.error("[PhaseActions] No access token available");
    return;
  }

  set(phasesStatusAtom, "loading");
  set(phasesErrorAtom, null);

  const result = await fetchValidatedArray(PhaseSchema, API_ROUTES.phases.getByWorkflow(workflowId), {
    jwt,
    context: `Workflow ${workflowId} Phases`,
  });

  if (result.success) {
    // Update phases atom directly (loading data, not user edit - no history)
    const currentPhases = get(_phasesAtom);
    const otherPhases = currentPhases.filter((p) => p.workflowId !== workflowId);
    const newPhases = [...otherPhases, ...result.data];
    set(_phasesAtom, newPhases);

    // Update DB state reference (for DELETE detection in auto-save)
    set(_phasesInDBAtom, newPhases);

    set(phasesStatusAtom, "success");
    console.log("[PhaseActions] Phases loaded for workflow:", workflowId, result.data.length);
  } else {
    console.error("[PhaseActions] Error loading phases:", result.error);
    set(phasesStatusAtom, "error");
    set(phasesErrorAtom, result.error);
  }
});

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Update phase name
 */
export const updatePhaseNameAtom = atom(
  null,
  async (get, set, { phaseId, name }: { phaseId: string; name: string }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, name } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase name updated (optimistic):", phaseId, name);
  },
);

/**
 * Update phase description
 */
export const updatePhaseDescriptionAtom = atom(
  null,
  async (get, set, { phaseId, description }: { phaseId: string; description: string | null }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, description } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase description updated (optimistic):", phaseId);
  },
);

/**
 * Update phase calls configuration
 */
export const updatePhaseCallsConfigurationAtom = atom(
  null,
  async (
    get,
    set,
    { phaseId, callsConfiguration }: { phaseId: string; callsConfiguration: Phase["callsConfiguration"] },
  ) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, callsConfiguration } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase calls configuration updated (optimistic):", phaseId);
  },
);

/**
 * Update phase call datas
 */
export const updatePhaseCallDatasAtom = atom(
  null,
  async (get, set, { phaseId, callDatas }: { phaseId: string; callDatas: Phase["callDatas"] }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, callDatas } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase call datas updated (optimistic):", phaseId, (callDatas || []).length);
  },
);

/**
 * Update phase advancement conditions
 */
export const updatePhaseAdvancementConditionsAtom = atom(
  null,
  async (
    get,
    set,
    { phaseId, advancementConditions }: { phaseId: string; advancementConditions: Phase["advancementConditions"] },
  ) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, advancementConditions } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase advancement conditions updated (optimistic):", phaseId);
  },
);

/**
 * Update phase alerts
 */
export const updatePhaseAlertsAtom = atom(
  null,
  async (get, set, { phaseId, alerts }: { phaseId: string; alerts: Phase["alerts"] }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, alerts } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase alerts updated (optimistic):", phaseId);
  },
);

/**
 * Link a knowledge base to a phase
 */
export const linkKnowledgeBaseToPhaseAtom = atom(
  null,
  async (get, set, { phaseId, knowledgeBaseId }: { phaseId: string; knowledgeBaseId: string }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, knowledgeBaseId } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Knowledge base linked to phase (optimistic):", phaseId, knowledgeBaseId);
  },
);

/**
 * Unlink knowledge base from a phase
 */
export const unlinkKnowledgeBaseFromPhaseAtom = atom(null, async (get, set, phaseId: string) => {
  // Optimistic update
  const phases = get(_phasesAtom);
  set(
    _phasesAtom,
    phases.map((p) => (p.id === phaseId ? { ...p, knowledgeBaseId: null } : p)),
  );

  // Add to history
  set(addToHistoryAtom);

  console.log("[PhaseActions] Knowledge base unlinked from phase (optimistic):", phaseId);
});

/**
 * Update phase (generic) - For complex updates or immediate save
 *
 * Use this when:
 * - You need to update multiple fields at once
 * - You need immediate save (bypass auto-save)
 * - You're updating complex nested data
 *
 * For simple single-field updates, prefer specific atoms above.
 */
export const updatePhaseAtom = atom(
  null,
  async (
    get,
    set,
    {
      phaseId,
      payload,
      immediate = false,
    }: {
      phaseId: string;
      payload: UpdatePhaseApiPayload;
      immediate?: boolean;
    },
  ): Promise<Phase | null> => {
    const jwt = get(accessTokenAtom);
    if (!jwt) {
      console.error("[PhaseActions] No access token available");
      return null;
    }

    // Optimistic update
    const phases = get(_phasesAtom);
    set(
      _phasesAtom,
      phases.map((p) => (p.id === phaseId ? { ...p, ...payload } : p)),
    );

    // Add to history
    set(addToHistoryAtom);

    if (immediate) {
      // Immediate save (bypass auto-save) with validation
      const result = await putValidated(PhaseSchema, API_ROUTES.phases.update(phaseId), payload, {
        jwt,
        context: `Update Phase ${phaseId}`,
      });

      if (result.success) {
        // Update with validated server response
        set(
          _phasesAtom,
          phases.map((p) => (p.id === phaseId ? result.data : p)),
        );
        console.log("[PhaseActions] Phase updated (immediate):", phaseId);
        return result.data;
      } else {
        console.error("[PhaseActions] Error updating phase:", result.error);
        // Rollback optimistic update
        set(_phasesAtom, phases);
        throw new Error(result.error);
      }
    } else {
      // Dirty + history automatic
      console.log("[PhaseActions] Phase updated (optimistic):", phaseId);
      return null;
    }
  },
);

/**
 * Reorder phases
 */
export const reorderPhasesAtom = atom(
  null,
  async (get, set, { workflowId, phaseIds }: { workflowId: string; phaseIds: string[] }) => {
    // Optimistic update
    const phases = get(_phasesAtom);
    const workflowPhases = phases.filter((p) => p.workflowId === workflowId);
    const otherPhases = phases.filter((p) => p.workflowId !== workflowId);

    // Reorder and update orderIndex
    const reorderedPhases = phaseIds
      .map((id, index) => {
        const phase = workflowPhases.find((p) => p.id === id);
        if (!phase) return null;
        return { ...phase, orderIndex: index };
      })
      .filter(Boolean) as Phase[];

    // Update state
    set(_phasesAtom, [...otherPhases, ...reorderedPhases]);

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phases reordered (optimistic):", workflowId, phaseIds.length);
  },
);

/**
 * Update multiple phases at once (used by AI workflow generation)
 * Replaces all phases with the provided array
 * Follows the same pattern as other action atoms: update state + add to history
 */
export const updateMultiplePhasesAtom = atom(null, (get, set, newPhases: Phase[]) => {
  set(_phasesAtom, newPhases);

  // Add to history (same pattern as all other action atoms)
  set(addToHistoryAtom);

  console.log("[PhaseActions] Multiple phases updated:", newPhases.length);
});

/**
 * Create phase
 */
export const createPhaseAtom = atom(null, async (get, set, payload: CreatePhaseBody): Promise<Phase | null> => {
  const jwt = get(accessTokenAtom);
  if (!jwt) {
    console.error("[PhaseActions] No access token available");
    return null;
  }

  const result = await postValidated(PhaseSchema, API_ROUTES.phases.create, payload, { jwt, context: "Create Phase" });

  if (result.success) {
    // Update local state
    const phases = get(_phasesAtom);
    set(_phasesAtom, [...phases, result.data]);

    // Add to history
    set(addToHistoryAtom);

    console.log("[PhaseActions] Phase created:", result.data.id);
    return result.data;
  } else {
    console.error("[PhaseActions] Error creating phase:", result.error);
    throw new Error(result.error);
  }
});

/**
 * Delete phase
 *
 * Note: This is a permanent operation. Once auto-saved, it cannot be undone.
 * The UI should show a confirmation dialog before calling this.
 */
export const deletePhaseAtom = atom(null, async (get, set, phaseId: string): Promise<void> => {
  const jwt = get(accessTokenAtom);
  const phases = get(_phasesAtom);

  console.log("[PhaseActions] Deleting phase:", phaseId, "needsCreation:", needsCreation(phaseId));

  // 1. Optimistic delete from local state
  set(
    _phasesAtom,
    phases.filter((p) => p.id !== phaseId),
  );

  // Add to history
  set(addToHistoryAtom);

  // 2. If real UUID (not temp), delete from DB immediately
  if (!needsCreation(phaseId) && jwt) {
    // Real UUID - delete from DB
    console.log("[PhaseActions] Attempting DELETE API for phase:", phaseId);
    const result = await deleteValidated(API_ROUTES.phases.delete(phaseId), {
      jwt,
      context: `Delete Phase ${phaseId}`,
    });

    if (result.success) {
      // Remove from DB state to prevent auto-save from trying to delete again
      const dbPhases = get(_phasesInDBAtom);
      set(
        _phasesInDBAtom,
        dbPhases.filter((p) => p.id !== phaseId),
      );

      // Note: We keep the UUID in history. When undo is triggered, the phase with UUID
      // will be restored, and auto-save will detect it's not in DB and re-create it.
      console.log("[PhaseActions] Phase deleted from DB (can be restored with undo):", phaseId);
    } else {
      console.error("[PhaseActions] Error deleting phase:", phaseId, result.error);

      // If phase doesn't exist in DB (404 or "Invalid phase ID"), it's already gone
      // Don't rollback, just log and continue
      if (result.error.includes("Invalid phase ID") || result.error.includes("not found")) {
        console.warn("[PhaseActions] Phase doesn't exist in DB, treating as already deleted:", phaseId);
        // Phase is already removed from local state (line 303), so we're done
      } else {
        // Other errors: rollback
        set(_phasesAtom, phases);
        throw new Error(result.error);
      }
    }
  } else {
    // Temp phase - just removed from local state
    console.log("[PhaseActions] Temp phase removed:", phaseId);
  }
});

/**
 * Duplicate phase
 */
export const duplicatePhaseAtom = atom(null, async (get, set, phaseId: string): Promise<Phase | null> => {
  const phases = get(_phasesAtom);
  const phaseToDuplicate = phases.find((p) => p.id === phaseId);

  if (!phaseToDuplicate) {
    console.error("[PhaseActions] Phase not found:", phaseId);
    return null;
  }

  // Create payload from existing phase with new phaseKey
  const newPhaseKey = generatePhaseKey();

  const payload: CreatePhaseBody = {
    workflowId: phaseToDuplicate.workflowId,
    phaseKey: newPhaseKey,
    orderIndex: phaseToDuplicate.orderIndex + 1,
    name: `${phaseToDuplicate.name} (copie)`,
    description: phaseToDuplicate.description || undefined,
    knowledgeBaseId: phaseToDuplicate.knowledgeBaseId || undefined,
    callsConfiguration: phaseToDuplicate.callsConfiguration || {
      duration: 7,
      frequency: "daily" as const,
    },
    callDatas: phaseToDuplicate.callDatas || [],
    advancementConditions: phaseToDuplicate.advancementConditions || [],
    alerts: phaseToDuplicate.alerts || [],
  };

  return set(createPhaseAtom, payload);
});
